"use client";

import { AuthProvider } from "@refinedev/core";
import { signIn, signOut, signUp, getCurrentUser, confirmSignUp, resendSignUpCode, confirmResetPassword, resetPassword, fetchUserAttributes } from "aws-amplify/auth";
import '../config/cognito'; // Initialize Cognito configuration

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    try {
      const result = await signIn({ username: email, password });
      
      if (result.isSignedIn) {
        return {
          success: true,
          redirectTo: "/dashboard",
        };
      } else {
        // Handle cases where additional steps are required
        if (result.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
          console.log("🔍 CONFIRM_SIGN_UP step detected, initiating redirect flow...");
          
          // Try to resend verification code to help the user
          try {
            await resendSignUpCode({ username: email });
            console.log("✅ Verification code resent successfully");
          } catch (resendError) {
            console.warn("Could not resend verification code:", resendError);
          }
          
          return {
            success: true, // ✅ FIXED: Changed from false to true so Refine processes the redirect
            redirectTo: `/confirm-signup?email=${encodeURIComponent(email)}`,
            successNotification: {
              message: "Please verify your email",
              description: "We've sent a verification code to your email. Please enter it to complete your account setup.",
            },
          };
        }
        
        return {
          success: false,
          error: {
            message: "Additional authentication steps required. Please contact support if this continues.",
            name: "Authentication Error",
          },
        };
      }
    } catch (error: any) {
      console.error("Login error:", error);
      
      // Handle specific AWS Cognito errors
      if (error.name === "UserNotConfirmedException") {
        console.log("🔍 UserNotConfirmedException detected, initiating redirect flow...");
        
        // User registered but hasn't verified email
        // Try to resend verification code to help the user
          try {
            await resendSignUpCode({ username: email });
            console.log("✅ Verification code resent successfully");
          } catch (resendError) {
            console.warn("Could not resend verification code:", resendError);
          }
          
          // Return success with redirect - no error shown to user
          const redirectResult = {
            success: true,
            redirectTo: `/confirm-signup?email=${encodeURIComponent(email)}`,
            successNotification: {
              message: "Please verify your email",
              description: "We've sent a verification code to your email. Please enter it to complete your account setup.",
            },
          };
          
          console.log("🔄 Auth provider returning redirect result:", redirectResult);
          return redirectResult;
      }
      
      if (error.name === "UserAlreadyAuthenticatedException") {
        try {
          // Check if the user is actually authenticated
          const user = await getCurrentUser();
          if (user) {
            console.log("User is already authenticated, redirecting to dashboard");
            return {
              success: true,
              redirectTo: "/dashboard",
            };
          }
        } catch (checkError) {
          console.error("Error checking current user:", checkError);
        }
      }
      
      if (error.name === "NotAuthorizedException") {
        return {
          success: false,
          error: {
            message: "Invalid email or password. Please check your credentials and try again.",
            name: "Login Failed",
          },
        };
      }
      
      if (error.name === "UserNotFoundException") {
        return {
          success: false,
          error: {
            message: "No account found with this email address. Please register first.",
            name: "Account Not Found",
          },
        };
      }
      
      if (error.name === "TooManyRequestsException") {
        return {
          success: false,
          error: {
            message: "Too many login attempts. Please wait a few minutes and try again.",
            name: "Rate Limit Exceeded",
          },
        };
      }
      
      // Generic error fallback
      return {
        success: false,
        error: {
          message: error.message || "Login failed. Please try again or contact support if the problem persists.",
          name: error.name || "Authentication Error",
        },
      };
    }
  },

  logout: async () => {
    try {
      // Properly wait for signOut to complete to prevent race conditions
      await signOut();
      
      return {
        success: true,
        redirectTo: "/login",
      };
    } catch (error: any) {
      console.error("Logout error:", error);
      // Even if there's an error, consider logout successful for UX
      // The user will be redirected to login and forced to re-authentication
      return {
        success: true,
        redirectTo: "/login",
      };
    }
  },

  onError: async (error) => {
    console.error("Auth error:", error);
    return { error };
  },

  check: async () => {
    try {
      // Add a timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Authentication check timeout')), 3000);
      });

      const user = await Promise.race([
        getCurrentUser(),
        timeoutPromise
      ]);

      if (user) {
        return {
          authenticated: true,
        };
      }
    } catch (error) {
      console.log("User not authenticated:", error);
    }

    return {
      authenticated: false,
      redirectTo: "/login",
    };
  },

  getPermissions: async () => {
    try {
      const user = await getCurrentUser();
      if (user) {
        // Tutors always have 'tutor' role
        return "tutor";
      }
    } catch (error) {
      console.log("Cannot get permissions:", error);
    }
    return null;
  },

  getIdentity: async () => {
    try {
      const user = await getCurrentUser();
      if (user) {
        // Get user attributes to extract firstName and lastName
        let firstName = 'Tutor';
        let lastName = '';
        
        try {
          const attributes = await fetchUserAttributes();
          firstName = attributes.given_name || firstName;
          lastName = attributes.family_name || lastName;
        } catch (attrError) {
          console.log("Could not fetch user attributes:", attrError);
          // Fallback to email parsing if attributes not available
          firstName = user.signInDetails?.loginId?.split('@')[0] || firstName;
        }

        return {
          id: user.userId,
          email: user.signInDetails?.loginId || '',
          firstName,
          lastName,
          role: 'tutor',
        };
      }
    } catch (error) {
      console.log("Cannot get identity:", error);
    }
    return null;
  },

  register: async ({ email, password, firstName, lastName, accessCode }) => {
    try {
      // Validate access code before attempting registration (frontend only)
      if (!accessCode || accessCode !== 'TCPARTNER25') {
        return {
          success: false,
          error: {
            message: "Invalid access code. Please contact us for the correct access code.",
            name: "Access Code Error",
          },
        };
      }

      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            given_name: firstName,
            family_name: lastName,
            // Note: accessCode validated on frontend only, not sent to Cognito
          },
        },
      });

      if (result.isSignUpComplete) {
        return {
          success: true,
          redirectTo: "/profile-setup",
        };
      } else {
        // Need to confirm signup (check email)
        return {
          success: true,
          redirectTo: `/confirm-signup?email=${encodeURIComponent(email)}`,
          successNotification: {
            message: "Registration successful! Please check your email for verification code.",
            description: "You need to verify your email before logging in.",
          },
        };
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      return {
        success: false,
        error: {
          message: error.message || "Registration failed",
          name: error.name || "Registration Error",
        },
      };
    }
  },

  forgotPassword: async ({ email }) => {
    try {
      await resetPassword({ username: email });
      return {
        success: true,
        successNotification: {
          message: "Password reset instructions sent to your email",
          description: "Check your email for further instructions",
        },
      };
    } catch (error: any) {
      console.error("Forgot password error:", error);
      return {
        success: false,
        error: {
          message: error.message || "Failed to send reset instructions",
          name: error.name || "Forgot Password Error",
        },
      };
    }
  },

  updatePassword: async ({ password, confirmPassword }) => {
    try {
      if (password !== confirmPassword) {
        return {
          success: false,
          error: {
            message: "Passwords do not match",
            name: "Password Mismatch",
          },
        };
      }

      // For changing password after reset, you'd typically use confirmResetPassword
      // For changing password when logged in, you'd use updatePassword from auth
      // This is a simplified implementation
      
      return {
        success: true,
        successNotification: {
          message: "Password updated successfully",
        },
      };
    } catch (error: any) {
      console.error("Update password error:", error);
      return {
        success: false,
        error: {
          message: error.message || "Failed to update password",
          name: error.name || "Update Password Error",
        },
      };
    }
  },
}; 